import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  SearchableDropdownOption,
  SearchableDropdownProps,
  SearchableDropdownState,
} from "./searchableDropdown.types";
import {
  buildMultiSelectDisplay,
  findSelectedLabel,
} from "./searchableDropdown.utils";
import { normalizeText } from "utils/text.normalize.logic";
import { useOnClickOutside } from "hooks/useOnClickOutside";

export interface UseSearchableDropdownResult {
  containerRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  listboxId: string;
  labelId: string;
  baseId: string;
  size: "default" | "compact";
  isOpen: boolean;
  query: string;
  highlightedIndex: number;
  resolvedSelectedValues: string[];
  selectedValueSet: Set<string>;
  isFilled: boolean;
  placeholderText: string;
  selectedLabel: string;
  multiSelectDisplay: {
    displayLabel: string;
    extraCount: number;
    fullLabel: string;
    labels: string[];
  };
  filteredOptions: SearchableDropdownOption[];
  visualState: SearchableDropdownState;
  showExtraCountBadge: boolean;
  canClear: boolean;
  resolvedValue: string;
  openDropdown: () => void;
  closeDropdown: () => void;
  handleSelect: (option: SearchableDropdownOption) => void;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
}

let searchableDropdownId = 0;

/** Manages state, derived data, and handlers for SearchableDropdown. */
export const useSearchableDropdown = (
  props: SearchableDropdownProps,
  placeholderText: string,
): UseSearchableDropdownResult => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const idRef = useRef<string>();
  if (!idRef.current) {
    searchableDropdownId += 1;
    idRef.current = `searchable-dropdown-${searchableDropdownId}`;
  }
  const baseId = idRef.current;
  const listboxId = `${baseId}-listbox`;
  const labelId = `${baseId}-label`;

  const size = props.size ?? "default";

  const resolvedSelectedValues = useMemo(
    () => props.selectedValues ?? [],
    [props.selectedValues],
  );

  const isFilled = props.multiSelect
    ? resolvedSelectedValues.length > 0
    : Boolean(props.value);
  const multiSelectDisplay = useMemo(
    () => buildMultiSelectDisplay(props.options, resolvedSelectedValues),
    [props.options, resolvedSelectedValues],
  );
  const selectedLabel = useMemo(() => {
    if (props.multiSelect) {
      return multiSelectDisplay.displayLabel;
    }
    return findSelectedLabel(props.options, props.value);
  }, [
    props.multiSelect,
    multiSelectDisplay.displayLabel,
    props.options,
    props.value,
  ]);
  const showExtraCountBadge =
    !isOpen && Boolean(props.multiSelect) && multiSelectDisplay.extraCount > 0;
  const canClear = Boolean(props.onClear) && isFilled;

  const selectedValueSet = useMemo(
    () => new Set(resolvedSelectedValues),
    [resolvedSelectedValues],
  );

  const visualState = useMemo<SearchableDropdownState>(() => {
    if (props.disabled) return "disabled";
    if (props.hasError) return "error";
    if (isOpen) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [props.disabled, props.hasError, isOpen, isFilled]);

  const normalizedOptions = useMemo(
    () =>
      props.options.map((option) => ({
        option,
        normalizedLabel: normalizeText(option.label),
      })),
    [props.options],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return props.options;
    return normalizedOptions
      .filter((entry) => entry.normalizedLabel.includes(normalizedQuery))
      .map((entry) => entry.option);
  }, [normalizedOptions, props.options, query]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(-1);
    }
  }, [filteredOptions.length, highlightedIndex]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const optionId = `${listboxId}-option-${highlightedIndex}`;
    const target = document.getElementById(optionId);
    if (target) {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, isOpen, listboxId]);

  useOnClickOutside(
    [containerRef],
    () => {
      setIsOpen(false);
      setQuery(selectedLabel);
      props.onBlur?.();
    },
    { enabled: isOpen, eventType: "mousedown" },
  );

  const openDropdown = (): void => {
    if (props.disabled) return;
    setIsOpen(true);
    setQuery("");
    setHighlightedIndex(-1);
    props.onFocus?.();
  };

  const closeDropdown = (): void => {
    setIsOpen(false);
    setQuery("");
    props.onBlur?.();
  };

  const handleSelect = (option: SearchableDropdownOption): void => {
    if (props.disabled) return;
    if (props.multiSelect) {
      const next = new Set(resolvedSelectedValues);
      if (next.has(option.value)) next.delete(option.value);
      else next.add(option.value);
      props.onSelectedValuesChange?.(Array.from(next));
      setQuery("");
      return;
    }
    props.onChange?.(option.value);
    setQuery(option.label);
    setIsOpen(false);
    props.onBlur?.();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (props.disabled) return;
    if (!isOpen) setIsOpen(true);
    setQuery(event.target.value);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (props.disabled) return;
    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter")) {
      event.preventDefault();
      openDropdown();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (filteredOptions.length === 0) return -1;
        if (prev < 0) return 0;
        return Math.min(prev + 1, filteredOptions.length - 1);
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (filteredOptions.length === 0) return -1;
        if (prev < 0) return filteredOptions.length - 1;
        return Math.max(prev - 1, 0);
      });
    } else if (event.key === "Enter") {
      event.preventDefault();
      const selected =
        highlightedIndex >= 0 ? filteredOptions[highlightedIndex] : undefined;
      if (selected) handleSelect(selected);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };

  const resolvedValue = isOpen ? query : selectedLabel;

  return {
    containerRef,
    inputRef,
    listboxId,
    labelId,
    baseId,
    size,
    isOpen,
    query,
    highlightedIndex,
    resolvedSelectedValues,
    selectedValueSet,
    isFilled,
    placeholderText,
    selectedLabel,
    multiSelectDisplay,
    filteredOptions,
    visualState,
    showExtraCountBadge,
    canClear,
    resolvedValue,
    openDropdown,
    closeDropdown,
    handleSelect,
    handleInputChange,
    handleKeyDown,
    setQuery,
    setHighlightedIndex,
  };
};
